import React, { useState, useEffect, useCallback, useRef } from 'react';
import { confirmar } from '../components/confirmacao';
import apiService from '../services/apiService';
import './MetasConfig.css';

const brDate = (iso) => {
    if (!iso) return '—';
    const p = String(iso).split('T')[0].split('-');
    return p.length === 3 ? `${p[2]}/${p[1]}/${p[0]}` : iso;
};

const SCOPES = [
    { key: 'company', label: 'Empresa toda (rede)' },
    { key: 'unit', label: 'Unidade' },
    { key: 'store', label: 'Loja' },
    { key: 'salesman', label: 'Vendedor' },
];
const SCOPE_LABEL = Object.fromEntries(SCOPES.map((s) => [s.key, s.label]));

const POINT_TYPES = [
    { key: 'all', label: 'Todos os pontos' },
    { key: 'behaviors', label: 'Comportamentos' },
    { key: 'valid_recordings', label: 'Atendimentos (válido + inválido)' },
];
const TYPE_LABEL = Object.fromEntries(POINT_TYPES.map((t) => [t.key, t.label]));

const emptyForm = {
    id: null,
    scope: 'store',
    store_id: '',
    unit_id: '',
    salesman_id: '',
    point_type: 'all',
    behavior_key: '',
    limit_points: '',
    window_start: '',
    window_end: '',
    label: '',
    active: true,
};

// Mesmas regras do POST /points-review/caps/save (a API continua sendo a autoridade).
export function validarTeto(f) {
    if (f.scope === 'store' && !f.store_id) return 'Selecione a loja';
    if (f.scope === 'unit' && !f.unit_id) return 'Selecione a unidade';
    if (f.scope === 'salesman' && !f.salesman_id) return 'Selecione o vendedor';
    const lim = Number(f.limit_points);
    if (f.limit_points === '' || !Number.isFinite(lim) || lim <= 0) return 'O limite deve ser maior que zero';
    if (!f.window_start || !f.window_end) return 'Informe o início e o fim da janela';
    if (f.window_end < f.window_start) return 'O fim da janela não pode ser anterior ao início';
    return null;
}

/**
 * Aba Tetos (teto de pontos). O ScopeSelector só cobre empresa/loja/fase, então o escopo do TETO
 * (empresa/unidade/loja/vendedor) é um campo do próprio formulário; da barra de escopo usamos só a
 * empresa (`scope.subdomain`).
 *
 * Semântica: limite DURO de pontos contabilizados. Acima dele o ponto continua registrado, mas sai
 * como excedente (uncounted_points). Tetos empilham: o mais específico consome primeiro. Hora
 * gravada não entra no teto.
 */
function TetosConfig({ user, scope }) {
    const subdomain = scope?.subdomain || '';
    const [toast, setToast] = useState(null);
    const notify = useCallback((msg, type = 'info') => {
        setToast({ msg, type });
        setTimeout(() => setToast(null), 4000);
    }, []);

    const [caps, setCaps] = useState([]);
    const [loading, setLoading] = useState(false);
    const [stores, setStores] = useState([]);
    const [salesmen, setSalesmen] = useState([]);
    const [units, setUnits] = useState([]);
    const [behaviors, setBehaviors] = useState([]);

    const [formOpen, setFormOpen] = useState(false);
    const [form, setForm] = useState(emptyForm);
    const [saving, setSaving] = useState(false);
    const savingRef = useRef(false);

    // Listas de apoio ao trocar de empresa (lojas, vendedores, unidades e comportamentos).
    useEffect(() => {
        setStores([]); setSalesmen([]); setUnits([]); setBehaviors([]); setCaps([]); setFormOpen(false);
        if (!subdomain) return undefined;
        let vivo = true;
        (async () => {
            try {
                const st = await apiService.promptEditorGetStores(subdomain);
                if (vivo) setStores(Array.isArray(st) ? st : []);
            } catch (e) { notify('Erro ao carregar lojas', 'error'); }
            try {
                const r = await apiService.pointsSalesmen({ userId: user?.user_id, subdomain });
                if (vivo) setSalesmen(r.salesmen || []);
            } catch (e) { /* silencioso: só o escopo vendedor depende disso */ }
            try {
                // unidades e comportamentos vêm da mesma listagem que a tela de Comunicados usa
                const lst = await apiService.communicationsList({
                    subdomain, types: ['banner'], include_inactive: false, include_expired: false,
                });
                if (vivo) { setUnits(lst.units || []); setBehaviors(lst.behaviors || []); }
            } catch (e) { /* silencioso: só os escopos unidade/comportamento dependem disso */ }
        })();
        return () => { vivo = false; };
    }, [subdomain, user?.user_id, notify]);

    const loadCaps = useCallback(async () => {
        if (!subdomain) return;
        setLoading(true);
        try {
            const r = await apiService.pointsCapsList({ subdomain });
            if (!r || !Array.isArray(r.caps)) throw new Error('resposta fora de forma');
            setCaps(r.caps);
        } catch (e) {
            notify(e.response?.data?.detail || 'Erro ao carregar tetos', 'error');
        } finally {
            setLoading(false);
        }
    }, [subdomain, notify]);

    useEffect(() => { loadCaps(); }, [loadCaps]);

    const setField = (k, v) => setForm((prev) => ({ ...prev, [k]: v }));
    const closeForm = () => { setFormOpen(false); setForm(emptyForm); };
    const openNew = () => { setForm(emptyForm); setFormOpen(true); };
    const openEdit = (c) => {
        setForm({
            id: c.id,
            scope: c.scope,
            store_id: c.store_id != null ? String(c.store_id) : '',
            unit_id: c.unit_id != null ? String(c.unit_id) : '',
            salesman_id: c.salesman_id != null ? String(c.salesman_id) : '',
            point_type: c.point_type,
            behavior_key: c.behavior_key || '',
            limit_points: c.limit_points ?? '',
            window_start: String(c.window_start || '').split('T')[0],
            window_end: String(c.window_end || '').split('T')[0],
            label: c.label || '',
            active: !!c.active,
        });
        setFormOpen(true);
    };

    const erro = validarTeto(form);

    const save = async () => {
        if (erro) { notify(erro, 'error'); return; }
        if (savingRef.current) return;
        savingRef.current = true;
        setSaving(true);
        try {
            const r = await apiService.pointsCapsSave({
                id: form.id || undefined,
                subdomain,
                scope: form.scope,
                // o alvo que não pertence ao escopo TEM de ir nulo: a API recusa campo sobrando
                store_id: form.scope === 'store' ? Number(form.store_id) : null,
                unit_id: form.scope === 'unit' ? Number(form.unit_id) : null,
                salesman_id: form.scope === 'salesman' ? Number(form.salesman_id) : null,
                point_type: form.point_type,
                behavior_key: form.point_type === 'behaviors' && form.behavior_key ? form.behavior_key : null,
                limit_points: Number(form.limit_points),
                window_start: form.window_start,
                window_end: form.window_end,
                label: form.label.trim() || null,
                active: form.active,
            });
            if (!r || r.success !== true) throw new Error('resposta fora de forma');
            notify(form.id ? 'Teto atualizado' : 'Teto criado', 'success');
            closeForm();
            loadCaps();
        } catch (e) {
            notify(e.response?.data?.detail || 'Erro ao salvar teto', 'error');
        } finally {
            savingRef.current = false;
            setSaving(false);
        }
    };

    const alvoTexto = (c) => {
        if (c.scope === 'company') return 'Empresa toda';
        if (c.scope === 'store') return c.store_name || `Loja ${c.store_id}`;
        if (c.scope === 'salesman') return c.salesman_name || `Vendedor ${c.salesman_id}`;
        const u = units.find((x) => String(x.id) === String(c.unit_id));
        return u?.label || `Unidade ${c.unit_id}`;
    };
    const comportamentoTexto = (c) => {
        if (c.point_type !== 'behaviors') return '—';
        if (!c.behavior_key) return <em>Todos</em>;
        return behaviors.find((b) => b.behavior_key === c.behavior_key)?.label || c.behavior_key;
    };

    const remove = async (c) => {
        if (!(await confirmar(`Excluir o teto de ${alvoTexto(c)} (${brDate(c.window_start)}–${brDate(c.window_end)})? O excedente volta a contar.`, { titulo: 'Excluir teto', perigo: true, rotuloConfirmar: 'Excluir' }))) return;
        try {
            await apiService.pointsCapsDelete({ subdomain, id: c.id });
            notify('Teto excluído', 'success');
            loadCaps();
        } catch (e) {
            notify(e.response?.data?.detail || 'Erro ao excluir teto', 'error');
        }
    };

    return (
        <div className="mc-page">
            {toast && <div className={`mc-toast mc-toast-${toast.type}`}>{toast.msg}</div>}

            <p className="mc-nota">
                O teto é um limite <strong>duro</strong> de pontos contabilizados na janela. Acima dele o
                ponto continua registrado, mas sai como <strong>excedente</strong> (não soma). Tetos
                empilham: o mais específico consome primeiro. Hora gravada fica fora do teto.
            </p>

            <div className="mc-filters">
                <div className="mc-field mc-filters-actions">
                    <button className="mc-btn mc-btn-primary" onClick={openNew} disabled={!subdomain}>➕ Novo teto</button>
                </div>
            </div>

            {formOpen && (
                <div className="mc-form-panel">
                    <h2>{form.id ? 'Editar teto' : 'Novo teto'}</h2>
                    <div className="mc-form-grid">
                        <div className="mc-field">
                            <label>Escopo *</label>
                            <select
                                value={form.scope}
                                onChange={(e) => setForm((p) => ({ ...p, scope: e.target.value, store_id: '', unit_id: '', salesman_id: '' }))}
                            >
                                {SCOPES.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
                            </select>
                        </div>
                        {form.scope === 'store' && (
                            <div className="mc-field">
                                <label>Loja *</label>
                                <select value={form.store_id} onChange={(e) => setField('store_id', e.target.value)}>
                                    <option value="">— Selecione —</option>
                                    {stores.map((s) => <option key={s.id} value={s.id}>{s.display_name || s.name}</option>)}
                                </select>
                            </div>
                        )}
                        {form.scope === 'unit' && (
                            <div className="mc-field">
                                <label>Unidade *</label>
                                <select value={form.unit_id} onChange={(e) => setField('unit_id', e.target.value)}>
                                    <option value="">— Selecione —</option>
                                    {units.map((u) => <option key={u.id} value={u.id}>{u.label}</option>)}
                                </select>
                            </div>
                        )}
                        {form.scope === 'salesman' && (
                            <div className="mc-field">
                                <label>Vendedor *</label>
                                <select value={form.salesman_id} onChange={(e) => setField('salesman_id', e.target.value)}>
                                    <option value="">— Selecione —</option>
                                    {salesmen.map((s) => (
                                        <option key={s.salesman_id} value={s.salesman_id}>
                                            {s.salesman_name} — {s.store_name}{s.is_active ? '' : ' (inativo)'}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        )}
                        <div className="mc-field">
                            <label>Tipo de ponto *</label>
                            <select
                                value={form.point_type}
                                onChange={(e) => setForm((p) => ({ ...p, point_type: e.target.value, behavior_key: '' }))}
                            >
                                {POINT_TYPES.map((t) => <option key={t.key} value={t.key}>{t.label}</option>)}
                            </select>
                        </div>
                        {form.point_type === 'behaviors' && (
                            <div className="mc-field">
                                <label>Comportamento (opcional)</label>
                                <select value={form.behavior_key} onChange={(e) => setField('behavior_key', e.target.value)}>
                                    <option value="">— Todos os comportamentos —</option>
                                    {behaviors.map((b) => <option key={b.behavior_key} value={b.behavior_key}>{b.label}</option>)}
                                </select>
                            </div>
                        )}
                        <div className="mc-field">
                            <label>Limite de pontos *</label>
                            <input type="number" min="0" step="any" value={form.limit_points} onChange={(e) => setField('limit_points', e.target.value)} />
                        </div>
                        <div className="mc-field">
                            <label>Janela: início *</label>
                            <input type="date" value={form.window_start} onChange={(e) => setField('window_start', e.target.value)} />
                        </div>
                        <div className="mc-field">
                            <label>Janela: fim *</label>
                            <input type="date" value={form.window_end} onChange={(e) => setField('window_end', e.target.value)} />
                        </div>
                        <div className="mc-field">
                            <label>Rótulo (opcional)</label>
                            <input type="text" value={form.label} onChange={(e) => setField('label', e.target.value)} />
                        </div>
                        <div className="mc-field">
                            <label>
                                <input type="checkbox" checked={form.active} onChange={(e) => setField('active', e.target.checked)} /> Ativo
                            </label>
                        </div>
                    </div>
                    {erro && <p className="mc-nota">{erro}</p>}
                    <div className="mc-form-actions">
                        <button className="mc-btn" onClick={closeForm} disabled={saving}>Cancelar</button>
                        <button className="mc-btn mc-btn-primary" onClick={save} disabled={saving || !!erro}>
                            {saving ? 'Salvando…' : 'Salvar'}
                        </button>
                    </div>
                </div>
            )}

            <div className="mc-list">
                <h2>Tetos cadastrados {loading ? '(carregando…)' : `(${caps.length})`}</h2>
                {!subdomain && <p className="mc-empty">Selecione uma empresa para ver os tetos.</p>}
                {subdomain && !loading && caps.length === 0 && <p className="mc-empty">Nenhum teto cadastrado.</p>}
                {caps.length > 0 && (
                    <table className="mc-table">
                        <thead>
                            <tr>
                                <th>Escopo</th><th>Alvo</th><th>Tipo</th><th>Comportamento</th>
                                <th>Limite</th><th>Início</th><th>Fim</th><th>Ativo</th><th></th>
                            </tr>
                        </thead>
                        <tbody>
                            {caps.map((c) => (
                                <tr key={c.id}>
                                    <td>{SCOPE_LABEL[c.scope] || c.scope}</td>
                                    <td>{alvoTexto(c)}{c.label ? <small> — {c.label}</small> : null}</td>
                                    <td>{TYPE_LABEL[c.point_type] || c.point_type}</td>
                                    <td>{comportamentoTexto(c)}</td>
                                    <td>{c.limit_points}</td>
                                    <td>{brDate(c.window_start)}</td>
                                    <td>{brDate(c.window_end)}</td>
                                    <td>{c.active ? 'Sim' : 'Não'}</td>
                                    <td className="mc-actions">
                                        <button className="mc-btn mc-btn-sm" onClick={() => openEdit(c)}>✏️ Editar</button>
                                        <button className="mc-btn mc-btn-sm mc-btn-danger" onClick={() => remove(c)}>🗑️ Excluir</button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>
        </div>
    );
}

export default TetosConfig;
